import { useCallback, useState } from 'react';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import normalizeAirspaceSummary from './hooks/useNormalize';
import useTranslation from './hooks/useTranslation';

import { AIRSPACE_API_HOST, DEFAULT_RADIUS_M, JSON_HEADERS, SUMMARY_PATH } from './constants';
import type { AirspaceSummary, FetchAirspaceSummary, UseAirspaceSummaryReturn } from './types';

const useAirspaceSummary = (): UseAirspaceSummaryReturn => {
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<AirspaceSummary | null>(null);
    const { authenticatedGet } = useFetch({ apiHost: AIRSPACE_API_HOST, headers: JSON_HEADERS });
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR } = useTranslation();

    const fetchSummary: FetchAirspaceSummary = useCallback(
        async ({ lat, lng, radiusM = DEFAULT_RADIUS_M }) => {
            try {
                // Keep the previous summary visible while the new one loads so the sheet
                // stays up across clicks. AirspaceSheet gates weather display on
                // isLoading so no stale-summary / fresh-weather mismatch flashes.
                setIsLoading(true);

                // include_tfr=false: TFR coverage is NDA-only (8 records, threshold is 20+),
                // so the API fail-closes with 503 when include_tfr=true (default). Passing
                // false returns 200 with data_coverage_warnings[] for the TFR gap. Auto-
                // flips on when Phase 4 NOTAM API lands per backend's 2026-04-18 comms.
                const url = `${SUMMARY_PATH}?lat=${lat}&lng=${lng}&radius=${radiusM}&include_tfr=false`;
                const raw = await withRetry(
                    () => authenticatedGet<unknown>(url),
                    CONNECTION_ERROR,
                    () => fetchSummary({ lat, lng, radiusM })
                );

                if (!raw) {
                    return null;
                }

                const normalized = normalizeAirspaceSummary(raw, { lat, lng });

                setData(normalized);

                return normalized;
            } finally {
                setIsLoading(false);
            }
        },
        [authenticatedGet, withRetry, CONNECTION_ERROR]
    );

    return { data, fetchSummary, isLoading };
};

export default useAirspaceSummary;
